import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { urlDaApi } from './api';
import { cofre, ehNativo } from './nativo';

/**
 * Portal do Cliente (Onda 1): tempo real do app. Lê o SSE do portal (`/api/portal/v1/tempo-real`) por `fetch` com leitura
 * em fluxo (funciona no navegador e no app nativo, que precisa do cabeçalho Bearer; o `EventSource` não manda cabeçalho).
 * Cada sinal (`pago`, `fatura`, `entrega`, `relatorio`, `mensagem`) invalida as consultas que mostram aquilo: o "Pago" aparece sozinho.
 * Caiu ou o servidor pediu `renovar`: reabre com espera crescente (até 30 s).
 */
export function separarEventos(buffer: string): { eventos: { tipo: string; dados: Record<string, unknown> }[]; resto: string } {
  const blocos = buffer.split('\n\n');
  const resto = blocos.pop() ?? '';
  const eventos: { tipo: string; dados: Record<string, unknown> }[] = [];
  for (const b of blocos) {
    let tipo = 'message';
    let dados = '';
    for (const linha of b.split('\n')) {
      if (linha.startsWith('event: ')) tipo = linha.slice(7).trim();
      else if (linha.startsWith('data: ')) dados += linha.slice(6);
    }
    if (!dados) continue;
    try {
      eventos.push({ tipo, dados: JSON.parse(dados) as Record<string, unknown> });
    } catch {
      /* evento malformado: ignora */
    }
  }
  return { eventos, resto };
}

export const CONSULTAS_DO_SINAL: Record<string, string[][]> = {
  pago: [['portal', 'faturas'], ['portal', 'fatura'], ['portal', 'hoje']],
  fatura: [['portal', 'faturas'], ['portal', 'hoje']],
  entrega: [['portal', 'entregas'], ['portal', 'hoje']],
  // E1: relatório publicado ou retirado pela equipe.
  relatorio: [['portal', 'relatorios'], ['portal', 'relatorio']],
  mensagem: [['portal', 'mensagens'], ['portal', 'hoje']],
};

export function useTempoRealPortal(ativo: boolean, aoSinal?: (tipo: string, dados: Record<string, unknown>) => void) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!ativo) return;
    let parar = false;
    let controle: AbortController | null = null;
    let espera = 1000;
    async function ligar() {
      while (!parar) {
        controle = new AbortController();
        try {
          const headers: Record<string, string> = { Accept: 'text/event-stream' };
          if (ehNativo()) {
            const token = await cofre.lerToken();
            if (token) headers.Authorization = `Bearer ${token}`;
          }
          const r = await fetch(new URL(urlDaApi('/tempo-real'), window.location.origin).toString(), { headers, credentials: ehNativo() ? 'omit' : 'same-origin', signal: controle.signal });
          if (!r.ok || !r.body) throw new Error(`tempo real ${r.status}`);
          espera = 1000;
          const leitor = r.body.getReader();
          const dec = new TextDecoder();
          let buffer = '';
          for (;;) {
            const { value, done } = await leitor.read();
            if (done) break;
            buffer += dec.decode(value, { stream: true });
            const { eventos, resto } = separarEventos(buffer);
            buffer = resto;
            for (const e of eventos) {
              for (const chave of CONSULTAS_DO_SINAL[e.tipo] ?? []) void qc.invalidateQueries({ queryKey: chave });
              aoSinal?.(e.tipo, e.dados);
            }
          }
        } catch {
          if (parar) return;
          espera = Math.min(espera * 2, 30_000);
        }
        if (!parar) await new Promise((res) => setTimeout(res, espera));
      }
    }
    void ligar();
    return () => {
      parar = true;
      controle?.abort();
    };
  }, [ativo, qc, aoSinal]);
}
